import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { longHourInterviewApi } from "../../services/longHourInterviewApi";
import { usePermissions } from "../../hooks/usePermissions";
import { StatusBadge } from "../../components/StatusBadge";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { useLocalization } from "../../hooks/useLocalization";

const STATUSES = [
  "TRIGGERED", "EMPLOYEE_NOTIFIED", "INTERVIEW_OFFERED", "EMPLOYEE_REQUESTED", "APPOINTMENT_SCHEDULED",
  "COMPLETED", "EMPLOYMENT_MEASURE_REVIEW", "FOLLOW_UP_REQUIRED", "CLOSED",
];

export default function LongHourInterviewCandidatesPage() {
  const { t } = useLocalization();
  const { canViewLongHourInterview } = usePermissions();
  const [candidates, setCandidates] = useState([]);
  const [status, setStatus] = useState("");

  const load = useCallback(() => {
    longHourInterviewApi.listCandidates(status ? { status } : {}).then((r) => setCandidates(r.data)).catch(() => {});
  }, [status]);
  useEffect(() => { if (canViewLongHourInterview) load(); }, [canViewLongHourInterview, load]);

  if (!canViewLongHourInterview) return <p className="text-sm text-gray-500" data-testid="long-hour-access-denied">{t("common.accessDenied")}</p>;

  return (
    <div className="space-y-5" data-testid="long-hour-candidates-page">
      <PageHelpBanner pageKey="long-hour-candidates" title={t("pageHelp.defaultTitle")}>
        <p dangerouslySetInnerHTML={{ __html: t("mgmtLongHourInterview.candidatesHelp") }} />
      </PageHelpBanner>
      <div>
        <Link to="/management/compliance/long-hour-interviews" className="text-xs font-semibold text-blue-600 hover:underline" data-testid="long-hour-back-link">&larr; {t("mgmtLongHourInterview.backToDashboard")}</Link>
        <h2 className="text-2xl font-bold tracking-tight text-gray-900 mt-1">{t("mgmtLongHourInterview.candidates")}</h2>
      </div>

      <div className="flex gap-2 flex-wrap">
        <button data-testid="long-hour-candidates-filter-ALL" onClick={() => setStatus("")} className={`px-2.5 py-1 rounded-md text-xs font-medium border ${!status ? "bg-gray-800 text-white border-gray-800" : "border-gray-200 bg-white hover:bg-gray-50 text-gray-600"}`}>{t("mgmtLongHourInterview.all")}</button>
        {STATUSES.map((s) => (
          <button key={s} data-testid={`long-hour-candidates-filter-${s}`} onClick={() => setStatus(s)}
            className={`px-2.5 py-1 rounded-md text-xs font-medium border ${status === s ? "bg-gray-800 text-white border-gray-800" : "border-gray-200 bg-white hover:bg-gray-50 text-gray-600"}`}>
            {statusLabel(t, s)}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-gray-50 text-gray-500 text-left">
            <tr><th className="px-4 py-3 font-medium">{t("mgmtLongHourInterview.colPeriod")}</th><th className="px-4 py-3 font-medium">{t("mgmtLongHourInterview.colHours")}</th><th className="px-4 py-3 font-medium">{t("mgmtLongHourInterview.colTriggerDate")}</th><th className="px-4 py-3 font-medium">{t("common.status")}</th><th className="px-4 py-3"></th></tr>
          </thead>
          <tbody>
            {candidates.map((c) => (
              <tr key={c.id} className="border-t border-gray-100" data-testid={`long-hour-candidate-row-${c.id}`}>
                <td className="px-4 py-2 text-gray-700">{c.monitoring_period}</td>
                <td className="px-4 py-2 text-gray-500 text-xs">{c.approved_hours}h</td>
                <td className="px-4 py-2 text-gray-500 text-xs">{c.trigger_date}</td>
                <td className="px-4 py-2"><StatusBadge status={c.status} /></td>
                <td className="px-4 py-2"><Link to={`/management/compliance/long-hour-interviews/${c.id}`} className="text-xs font-semibold text-blue-600 hover:underline" data-testid={`long-hour-candidate-view-${c.id}`}>{t("mgmtLongHourInterview.view")}</Link></td>
              </tr>
            ))}
            {candidates.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-500" data-testid="no-long-hour-candidates">{t("mgmtLongHourInterview.noCandidatesFilter")}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
